/**
 * 熊本県 名物めし 12段階（コンテンツデータ）。
 *
 * 数値（radius / mass / score / unlockScore）は他県と同じテンプレート。
 *
 * 調査で出てきた「書いてはいけないこと」を反映している。
 *  - 「デコポン」は品種名ではない。品種は「不知火」で、糖度とクエン酸の基準を
 *    満たしたものだけがデコポンの名で出荷される。表記は「デコポン（不知火）」。
 *    糖度13度以上という数値は九州農政局の資料にない。書かない。
 *  - からし蓮根の「1632年に和尚が考案」、馬刺しの「加藤清正の朝鮮出兵が始まり」、
 *    いきなり団子の名の由来は、いずれも農水省が伝承形か複数説の併記。断定しない。
 *  - 馬刺しの発祥の店・発祥の年は公的資料になし。書けるのは「馬肉生産量日本一」と
 *    「桜肉と呼ばれるのは切り口が桜色だから」まで。
 *  - 「熊本ラーメンのルーツは玉名ラーメン」と断定しない。発祥・歴史の公式記述がない。
 *    特徴（豚骨＋鶏ガラ／中太麺／焦がしにんにく・マー油／キクラゲ）だけ書く。
 *  - 太平燕の「日本発祥」「熊本発祥」と書かない。農水省は「明治時代後期に華僑が
 *    伝えたといわれ」という伝聞形。
 *  - ゆうべにの「日本一」「収穫量全国◯位」は書かない。県の順位資料が古い。
 *  - 晩白柚の「世界一大きい柑橘」は公式資料で裏が取れなかったので採用しない。
 *
 * 出典の内訳は農水省8品、農水省GI1品、九州農政局1品、熊本県公式1品、熊本県観光連盟2品。
 *
 * 本番画像は public/assets/foods/kumamoto/ に置く。
 *
 * 2026-10-02 確定案（audit/_確定案.csv）で料理を入れ替えた。
 * - 外した品：とじこ豆・ゆうべに・焼鮎の甘露煮・鯛そうめん（店で食べられる品に差し替え）
 * - ひともじのぐるぐる：「細川重賢の時代に倹約令が出た際、安い酒のつまみとして考えだされた」は農水省ページにあるが、人物・時代にかかわる考案話なので、入れるなら「といわれる」の形にする。今回は文字数の都合で入れていない
 * - ひともじのぐるぐる：「ひともじ」の名は、ねぎを古く「葱（き）」の一文字で表したから、という説明が農水省の広報誌（official のURL）にある。熊本の一文字（わけぎ）の名の由来として言い切れるか確かめていないので書かない
 * - 阿蘇の高菜めし：「元祖たかなめし」は店の自称なので書かない
 * - 阿蘇の高菜めし：農水省の表記は「高菜飯」、熊本県観光連盟は「高菜めし」。本文は品名に合わせて「高菜めし」とした
 * - 天草大王の地鶏料理：「幻の地鶏」は県観光連盟の見出しの呼び名。使うなら「と呼ばれる」の形になる。今回は書いていない
 * - 天草大王の地鶏料理：復元の完成年は、県観光連盟は「2001年に復元に成功」、県農業研究センターは「平成5年（1993）から交配開始」と書き方が違う。完成年は書かず、交配開始の年だけを県のページから書いた
 * - 天草大王の地鶏料理：鶏の生食（刺身・たたき）は扱わない
 * - 天草のうに丼：季節ごとのウニの種類（夏はアカウニ、秋冬はガンガゼウニなど）は、公式の観光協会ページでは確かめられず、個人・店のサイトにしかなかったので書かない
 * - 天草のうに丼：「うに嫌いもうに好きになると言われる」は観光協会の宣伝文句なので書かない
 * - 天草のうに丼：ミョウバン不使用の話は公式ページで確かめられなかったので書かない
 */

import { attachHulls } from '../../utils/itemShape';
import type { RewardLink, StageDefinition, StageItemDefinition } from '../types';

import { KUMAMOTO_FOOD_HULLS } from './kumamotoFoodShapes.generated';
import { kumamotoSceneryTour } from './kumamotoScenes';

/** 「食べに行く」導線の既定リンク（特定店の宣伝にならないよう検索リンクにする）。 */
function searchLinks(area: string, dish: string): RewardLink[] {
  return [
    {
      label: '地図で探す',
      url: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${area} ${dish}`)}`,
    },
    {
      label: 'お店を調べる',
      url: `https://www.google.com/search?q=${encodeURIComponent(`${area} ${dish}`)}`,
    },
  ];
}

const FOOD_ITEMS: StageItemDefinition[] = [
  {
    id: 'hitomoji-guruguru',
    level: 1,
    name: 'ひともじのぐるぐる',
    shortName: 'ひともじのぐるぐる',
    image: '/assets/foods/kumamoto/01_hitomoji_guruguru.webp',
    radius: 17,
    mass: 1,
    score: 2,
    description: 'わけぎの別称「一文字（ひともじ）」をさっとゆでて冷まし、白根の部分を軸にして青い葉を巻きつける。白味噌・砂糖・酢・みりんで作る酢味噌や辛味噌をかけたり添えたりして食べる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/itimonji_no_guruguru_kumamoto.html',
    icon: { label: 'ぐるぐる', shape: 'berry', baseColor: '#5f9e3a', accentColor: '#e8d27a' },
    reward: {
      unlockScore: 200,
      area: '熊本県内全域',
      headline: 'ゆでたわけぎを白根に巻く',
      tip: '冬を越して寒さで甘みが増した春先が旬。かつてはひな祭りに、こんにゃくの刺身、マテ貝、ウバ貝の煮しめなどと並んで欠かせないものだった。',
      links: searchLinks('熊本市', '一文字のぐるぐる'),
      official: [{ label: '農林水産省 aff（あふ）2018年1月号「コンパクトなねぎを開発」', url: 'https://www.maff.go.jp/j/pr/aff/1801/characterinformation.html' }],
    },
  },
  {
    id: 'ikinari-dango',
    level: 2,
    name: 'いきなり団子',
    shortName: 'いきなり団子',
    image: '/assets/foods/kumamoto/02_ikinari_dango.webp',
    radius: 21,
    mass: 1.3,
    score: 4,
    description: '輪切りの芋を、生地で包んで蒸す。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/ikinari_dango_kumamoto.html',
    icon: { label: 'いきなり団子', shape: 'mochi', baseColor: '#f0e8d0', accentColor: '#d8a848' },
    reward: {
      unlockScore: 500,
      area: '大津町ほか県内各地',
      headline: '輪切りのさつまいもを小麦粉の生地で包んで蒸した、素朴なおやつ。',
      tip: '出来立てのあつあつが本来の食べ方。土産用は冷凍なので温めてから。',
      links: searchLinks('熊本県', 'いきなり団子'),
    },
  },
  {
    id: 'dekopon',
    level: 3,
    name: 'デコポン（不知火）',
    shortName: 'デコポン',
    image: '/assets/foods/kumamoto/03_dekopon.webp',
    radius: 26,
    mass: 1.7,
    score: 8,
    description: '基準を満たしたものだけが、この名で出る。',
    source:
      'https://www.maff.go.jp/kyusyu/seibibu/kokuei/28/attach/pdf/kankyougakusyukai-5.pdf',
    icon: { label: 'デコポン', shape: 'berry', baseColor: '#e88a28', accentColor: '#4a7a3a' },
    reward: {
      unlockScore: 1000,
      area: '宇城市・宇土市から玉名市',
      headline: '品種名は「不知火」。糖度とクエン酸の基準を満たしたものだけがデコポンの名で出荷される。',
      tip: '頭の出っぱりが目印。果皮がむきやすい。',
      links: searchLinks('熊本県', 'デコポン'),
    },
  },
  {
    id: 'aso-takana-meshi',
    level: 4,
    name: '阿蘇の高菜めし',
    shortName: '高菜めし',
    image: '/assets/foods/kumamoto/04_aso_takana_meshi.webp',
    radius: 30,
    mass: 2.2,
    score: 16,
    description: '阿蘇高菜の古漬けを細かく刻んで油で炒め、唐辛子を加えてご飯に混ぜる。阿蘇の郷土料理として親しまれている。古漬けは高菜を半年ほど乳酸発酵させたもの。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/aso_takana_zuke_kumamoto.html',
    icon: { label: '高菜めし', shape: 'rice-bowl', baseColor: '#7a7a3a', accentColor: '#e8c84a' },
    reward: {
      unlockScore: 1800,
      area: '阿蘇市',
      headline: '刻んで炒めた高菜をご飯に',
      tip: '阿蘇高菜は寒冷な気候と火山灰の大地が育む伝統野菜。3〜4月にとれる新芽は鮮度が落ちやすいので、昔から収穫後すぐに塩と赤唐辛子で漬物にしてきた。',
      links: searchLinks('阿蘇市', '高菜めし'),
      official: [{ label: '熊本県観光連盟「だご汁・高菜めし」', url: 'https://kumamoto.guide/brand/foods/foods_05.html' }],
    },
  },
  {
    id: 'karashi-renkon',
    level: 5,
    name: 'からし蓮根',
    shortName: 'からし蓮根',
    image: '/assets/foods/kumamoto/05_karashi_renkon.webp',
    radius: 36,
    mass: 2.8,
    score: 32,
    description: '切り口が、九曜紋に似ている。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/karashi_renkon_kumamoto.html',
    icon: { label: 'からし蓮根', shape: 'dumpling', baseColor: '#e0c048', accentColor: '#8a6a2a' },
    reward: {
      unlockScore: 3000,
      area: '熊本市・宇城地方',
      headline: 'れんこんの穴にからし味噌を詰めて揚げた惣菜。輪切りの断面が九曜紋に似る。',
      tip: '紋が見えるよう5ミリから1センチ幅に切る。そのまま食べるほか、醤油をつける家庭も。',
      links: searchLinks('熊本市', 'からし蓮根'),
    },
  },
  {
    id: 'taipien',
    level: 6,
    name: '太平燕',
    shortName: '太平燕',
    image: '/assets/foods/kumamoto/06_taipien.webp',
    radius: 42,
    mass: 3.5,
    score: 64,
    description: '揚げたゆで卵が、上にのる。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/taipiien_kumamoto.html',
    icon: { label: '太平燕', shape: 'ramen', baseColor: '#f0ead8', accentColor: '#d8a848' },
    reward: {
      unlockScore: 4500,
      area: '熊本市',
      headline: '春雨に炒めた野菜や豚肉を合わせ、揚げたゆで卵をのせた中華風のスープ。',
      tip: '春雨はのどごしのよい緑豆春雨が多い。スープは鶏ガラ・豚骨・塩・醤油など店ごとに違う。',
      links: searchLinks('熊本市', '太平燕'),
    },
  },
  {
    id: 'kumamoto-ramen',
    level: 7,
    name: '熊本ラーメン',
    shortName: '熊本ラーメン',
    image: '/assets/foods/kumamoto/07_kumamoto_ramen.webp',
    radius: 46,
    mass: 4.4,
    score: 128,
    description: '焦がしにんにくの香りが立つ。',
    source: 'https://kumamoto.guide/brand/foods/foods_01.html',
    icon: { label: '熊本ラーメン', shape: 'ramen', baseColor: '#e8dcc0', accentColor: '#5a3418' },
    reward: {
      unlockScore: 6500,
      area: '熊本市・玉名市',
      headline: '豚骨と鶏ガラのスープに中太麺。焦がしにんにくとキクラゲが入る。',
      tip: '焦がしにんにくやマー油がまろやかな豚骨と合わさる。キクラゲが必ず入る。',
      links: searchLinks('熊本市', '熊本ラーメン'),
    },
  },
  {
    id: 'dagojiru',
    level: 8,
    name: 'だご汁',
    shortName: 'だご汁',
    image: '/assets/foods/kumamoto/08_dagojiru.webp',
    radius: 51,
    mass: 5.4,
    score: 256,
    description: 'だごは、団子のこと。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/dagojiru_kumamoto.html',
    icon: { label: 'だご汁', shape: 'pot', baseColor: '#c8a870', accentColor: '#8a6a3a' },
    reward: {
      unlockScore: 9000,
      area: '熊本県内全域（阿蘇ほか）',
      headline: '小麦粉を練って手でちぎった団子と根菜を煮込んだ汁。だごは団子のこと。',
      tip: '寝かせた生地を手で延ばしてちぎる。いりこと干し椎茸のだしに味噌か醤油。',
      links: searchLinks('阿蘇', 'だご汁'),
    },
  },
  {
    id: 'amakusa-daio',
    level: 9,
    name: '天草大王の地鶏料理',
    shortName: '天草大王',
    image: '/assets/foods/kumamoto/09_amakusa_daio.webp',
    radius: 57,
    mass: 6.6,
    score: 512,
    description: '天草地方で昔から肉用に育てられてきた、国内最大級の体の大きさと重さをもつ地鶏「天草大王」の料理。肉は柔らかすぎず堅すぎず、ほどよい食べごたえで、くせがなくこくのあるうま味がある。',
    source:
      'https://kumamoto.guide/look/detail/251',
    icon: { label: '天草大王', shape: 'feast', baseColor: '#b8742e', accentColor: '#5a3a1a' },
    reward: {
      unlockScore: 12000,
      area: '上天草市ほか天草地方',
      headline: '大きな地鶏を味わう',
      tip: '明治中期に中国から入ったランシャン種を天草で肉用に改良した鶏。大正時代は博多水炊き用に出荷されたが、昭和の不況で需要が減り絶えた。平成5年から復元の交配が始まった。',
      links: searchLinks('上天草市', '天草大王'),
      official: [{ label: '熊本県農業研究センター「天草大王」', url: 'https://www.pref.kumamoto.jp/soshiki/75/851.html' }],
    },
  },
  {
    id: 'akaushi-don',
    level: 10,
    name: 'あか牛丼',
    shortName: 'あか牛丼',
    image: '/assets/foods/kumamoto/10_akaushi_don.webp',
    radius: 63,
    mass: 8,
    score: 1024,
    description: 'GI登録の、くまもとあか牛。',
    source: 'https://www.maff.go.jp/j/shokusan/gi_act/register/0067/index.html',
    icon: { label: 'あか牛丼', shape: 'roe', baseColor: '#c05a3a', accentColor: '#f0e0c8' },
    reward: {
      unlockScore: 16000,
      area: '阿蘇地域',
      headline: 'GI登録の「くまもとあか牛」を焼いて薄く切り、ご飯に敷きつめた丼。',
      tip: 'ミディアムレアに焼いて薄く切る。脂の少ないもも肉が向く。',
      links: searchLinks('阿蘇', 'あか牛丼'),
    },
  },
  {
    id: 'basashi',
    level: 11,
    name: '馬刺し',
    shortName: '馬刺し',
    image: '/assets/foods/kumamoto/11_basashi.webp',
    radius: 70,
    mass: 9.6,
    score: 2048,
    description: '切り口が桜色だから、桜肉。',
    source:
      'https://www.maff.go.jp/j/keikaku/syokubunka/k_ryouri/search_menu/menu/basashi_kumamoto.html',
    icon: { label: '馬刺し', shape: 'feast', baseColor: '#c0303a', accentColor: '#f0e8d8' },
    reward: {
      unlockScore: 22000,
      area: '熊本市・阿蘇地域',
      headline: '桜肉とも呼ばれる馬肉の刺身。農林水産省によると、熊本は馬肉の生産量が日本一。発祥には複数の説がある。',
      tip: 'たまねぎ・おろし生姜・おろしにんにく・小口切りの小ねぎを薬味に、甘口醤油で。',
      links: searchLinks('熊本市', '馬刺し'),
    },
  },
  {
    id: 'amakusa-uni-don',
    level: 12,
    name: '天草のうに丼',
    shortName: '天草うに丼',
    image: '/assets/foods/kumamoto/12_amakusa_uni_don.webp',
    radius: 78,
    mass: 11.4,
    score: 4096,
    description: '天草の海でとれた生うにをご飯にのせた丼。天草では3月上旬にムラサキウニ漁が解禁される。海の栄養たっぷりの海藻を食べて育つうには、臭みがなく甘いといわれる。',
    source:
      'https://www.t-island.jp/blog/19866',
    icon: { label: 'うに丼', shape: 'rice-bowl', baseColor: '#e8a23a', accentColor: '#3a5a2a' },
    reward: {
      unlockScore: 30000,
      area: '天草市／上天草市',
      headline: '春に解禁される天草のうに',
      tip: '天草の生うには漁師が素潜りでとる。もともと漁獲量が多くないうえ、天候が悪いと漁に出られず入荷のない日もある。春には生うにを味わう催しが天草各地で開かれる。',
      links: searchLinks('天草市', 'うに丼'),
      official: [{ label: '天草宝島観光協会「天草生うに三昧」', url: 'https://www.t-island.jp/special/uni' }],
    },
  },
];

/** 得点は 2 ^ level を基準にしている（GAME_TUNING.score で方式変更可能）。 */
export const kumamotoFoodStage: StageDefinition = {
  regionId: 'kumamoto',
  regionName: '熊本県',
  mode: 'food',
  title: '熊本めしマージ',
  theme: {
    primaryColor: '#6a4a2a',
    secondaryColor: '#d9a441',
    accentColor: '#e8c060',
    backgroundColor: '#171310',
  },
  /** 背景ツアー「熊本めぐり」。軸は季節。冬の海岸から始めて、一年かけて冬の熊本城へ。 */
  scenery: {
    tour: kumamotoSceneryTour,
  },
  items: attachHulls(FOOD_ITEMS, KUMAMOTO_FOOD_HULLS),
};
